import styles from './TextArea.module.scss';
import {
  forwardRef,
  type ReactNode,
  Ref,
  useRef,
  HTMLInputTypeAttribute,
  ChangeEvent,
  useContext,
  useEffect,
} from 'react';
import { useMergeRefs } from '@libs/react/ref';
import { InputShared } from './InputShared';
import { FormContext } from '../form/FormContext';
import { FormFieldContext } from '../form/FormFieldContext';

type Message = {
  content: string;
  type: 'error' | 'info';
};

type Props = {
  addon?: ReactNode;
  bottom?: ReactNode;
  defaultValue?: string | number;
  disabled?: boolean;
  grow?: boolean | number;
  id?: string;
  label?: string;
  max?: number;
  maxLength?: number;
  message?: Message;
  min?: number;
  name?: string;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onValue?: (value: string) => void;
  prefix?: ReactNode;
  required?: boolean;
  rows?: number;
  type?: HTMLInputTypeAttribute;
  valid?: boolean;
  value?: string;
  width?: string;
};

export const TextArea = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      addon,
      bottom,
      defaultValue,
      disabled,
      grow,
      label,
      max,
      maxLength,
      min,
      onBlur,
      onFocus,
      onKeyDown,
      prefix,
      required = false,
      rows = 1,
      type = 'text',
      width,
      onValue,
      valid,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const mergedRef = useMergeRefs<HTMLTextAreaElement>(ref, inputRef);
    const formContext = useContext(FormContext);
    const formFieldContext = useContext(FormFieldContext);
    const name = props.name ?? formFieldContext.name;

    useEffect(() => {
      if (name && inputRef.current) {
        const prev = formContext.inputs.get(name) ?? [];
        formContext.inputs.set(name, [...prev, inputRef.current]);
      }
      return () => {
        formContext.inputs.delete(name);
      };
    }, [formContext, name]);

    const onClick = () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };

    function onChangeTextarea(event: ChangeEvent<HTMLTextAreaElement>) {
      const value = event.currentTarget.value;
      props.onChange?.(event);
      onValue?.(value);
    }

    return (
      <InputShared
        bottom={bottom}
        end={addon}
        id={props.id}
        label={label}
        name={name}
        onClick={onClick}
        valid={valid}
      >
        {({ id }) => (
          <textarea
            ref={mergedRef as Ref<HTMLTextAreaElement>}
            className={styles['textarea-element']}
            defaultValue={defaultValue}
            disabled={disabled}
            id={id}
            maxLength={maxLength}
            name={name}
            onBlur={onBlur}
            onChange={onChangeTextarea}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            required={required}
            rows={rows}
          />
        )}
      </InputShared>
    );
  },
);
